<template>
	<div class="flex flex-col border-1 border-gray-300 rounded-md px-4 pt-3 mb-6" style="background: #fcfbfb">
		<div class="flex items-center justify-between">
			<label class="text-13 text-secondary">{{ quickFiltersTitle }}</label>
			<span v-if="enableTime" class="text-13 text-secondary cursor-pointer hover:text-error" @click="resetTime"
				>Reset Time</span
			>
		</div>
		<app-select
			class="w-full"
			v-model="selectedOption"
			:options="options"
			@select="selectOption"
			:can-deselect="false"
			:hide-label="true"
		/>
		<div class="grid grid-cols-2 my-2 gap-4">
			<app-datepicker
				class="w-full tight pt-2"
				:placeholder="startDatePlaceholder"
				:initial-value="modelValue[startDate]"
				@selected="onStartChange('date', $event)"
			/>
			<app-input
				v-if="enableTime"
				class="w-full tight pt-2"
				name="time"
				placeholder="Start Time"
				type="time"
				:modelValue="startTime"
				@change="onStartChange('time', $event.target.value)"
			/>
			<app-datepicker
				class="w-full tight pt-2"
				:placeholder="endDatePlaceholder"
				:initial-value="modelValue[endDate]"
				@selected="onEndChange('date', $event)"
				:position="enableTime ? 'left' : 'right'"
			/>
			<app-input
				v-if="enableTime"
				class="w-full tight pt-2"
				name="time"
				placeholder="End Time"
				type="time"
				:modelValue="endTime"
				@change="onEndChange('time', $event.target.value)"
			/>
		</div>
	</div>
</template>

<script>
	import moment from 'moment';
	import AppDatepicker from '@/components/app/AppDatepicker';
	import AppSelect from '@/components/app/AppSelect.vue';

	export default {
		name: 'AppAdvancedDateFilter',
		props: {
			modelValue: {},
			startDate: {},
			endDate: {},
			startDatePlaceholder: {
				type: String,
				default: 'Start Date',
			},
			endDatePlaceholder: {
				type: String,
				default: 'End Date',
			},
			enableTime: {
				type: Boolean,
				default: false,
			},
			quickFiltersTitle: {
				type: String,
				default: 'Quick Date Filters',
			},
			withOffset: {
				type: Boolean,
				default: true,
			},
		},
		components: { AppDatepicker, AppSelect },
		data: () => ({
			selectedOption: null,
			startTime: '00:00',
			endTime: '00:00',
			options: [
				{
					value: 0,
					startDate: moment().format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Today',
				},
				{
					value: -1,
					startDate: moment()
						.add(-1, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment().format('YYYY-MM-DD'),
					label: 'Yesterday',
				},
				{
					value: 7,
					startDate: moment()
						.startOf('week')
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.endOf('week')
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'This Week',
				},
				{
					value: -7,
					startDate: moment()
						.startOf('week')
						.add(-6, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.endOf('week')
						.add(-6, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last Week',
				},
				{
					value: -31,
					startDate: moment()
						.startOf('month')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.endOf('month')
						.format('YYYY-MM-DD'),
					label: 'This Month',
				},
				{
					value: -8,
					startDate: moment()
						.add(-7, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last 7 Days',
				},
				{
					value: -14,
					startDate: moment()
						.add(-14, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last 14 Days',
				},
				{
					value: -28,
					startDate: moment()
						.add(-28, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last 28 Days',
				},
				{
					value: -30,
					startDate: moment()
						.add(-30, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last 30 Days',
				},
				{
					value: -90,
					startDate: moment()
						.add(-90, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last 90 Days',
				},
				{
					value: -180,
					startDate: moment()
						.add(-180, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'Last 180 Days',
				},
				{
					value: -365,
					startDate: moment()
						.add(-365, 'day')
						.format('YYYY-MM-DD'),
					endDate: moment()
						.add(1, 'day')
						.format('YYYY-MM-DD'),
					label: 'A year',
				},
				{
					value: 'custom',
					startDate: null,
					endDate: null,
					label: 'Custom',
				},
			],
		}),
		methods: {
			setDates(option) {
				if (option.startDate && option.endDate) {
					const params = { ...this.modelValue };
					if (this.enableTime) {
						const startDateTime = this.setTime(option.startDate, this.startTime);
						const endDateTime = this.setTime(option.endDate, this.endTime);
						params[this.startDate] = startDateTime.format('YYYY-MM-DD HH:mm');
						params[this.endDate] = endDateTime.format('YYYY-MM-DD HH:mm');
					} else {
						params[this.startDate] = moment(option.startDate).format('YYYY-MM-DD');
						params[this.endDate] = moment(option.endDate).format('YYYY-MM-DD');
					}
					if (this.withOffset) params.offset = 0;
					this.$emit('update:modelValue', params);
				}
			},
			clearDates() {
				const params = { ...this.modelValue };
				params[this.startDate] = moment().format('YYYY-MM-DD');
				params[this.endDate] = moment()
					.add(1, 'day')
					.format('YYYY-MM-DD');
				if (this.withOffset) params.offset = 0;
				this.$emit('update:modelValue', params);
			},
			onStartChange(type, value) {
				const params = { ...this.modelValue };
				type === 'date' ? (params[this.startDate] = value) : (this.startTime = value);
				if (this.enableTime) {
					const dateTime = this.setTime(params[this.startDate], this.startTime);
					params[this.startDate] = dateTime.format('YYYY-MM-DD HH:mm');
				} else {
					params[this.startDate] = moment(params[this.startDate]).format('YYYY-MM-DD');
				}
				if (this.withOffset) params.offset = 0;
				this.$emit('update:modelValue', params);
			},
			onEndChange(type, value) {
				const params = { ...this.modelValue };
				type === 'date' ? (params[this.endDate] = value) : (this.endTime = value);
				if (this.enableTime) {
					const dateTime = this.setTime(params[this.endDate], this.endTime);
					params[this.endDate] = dateTime.format('YYYY-MM-DD HH:mm');
				} else {
					params[this.endDate] = moment(params[this.endDate]).format('YYYY-MM-DD');
				}
				if (this.withOffset) params.offset = 0;
				this.$emit('update:modelValue', params);
			},
			selectOption(newValue) {
				const selected = this.options.find((option) => option.value === newValue);
				if (selected) {
					if (newValue.value !== 'custom') {
						this.setDates(selected);
					} else {
						this.clearDates();
					}
				}
			},
			resetTime() {
				this.onStartChange('time', '00:00');
				this.$nextTick(() => {
					this.onEndChange('time', '00:00');
				});
			},
			setTime(date, time) {
				return moment(date).set({
					hour: moment(time, 'HH:mm').hours(),
					minute: moment(time, 'HH:mm').minutes(),
				});
			},
			initializeComponent() {
				let option = this.options.find(
					(option) =>
						moment(option.startDate).format('YYYY-MM-DD') ===
							moment(this.modelValue[this.startDate]).format('YYYY-MM-DD') &&
						moment(option.endDate).format('YYYY-MM-DD') ===
							moment(this.modelValue[this.endDate]).format('YYYY-MM-DD')
				);
				if (option) {
					this.selectedOption = option.value;
				} else {
					this.selectedOption = 'custom';
				}
				if (this.modelValue[this.startDate]) {
					this.startTime = moment(this.modelValue[this.startDate]).format('HH:mm');
				}
				if (this.modelValue[this.endDate]) {
					this.endTime = moment(this.modelValue[this.endDate]).format('HH:mm');
				}
			},
		},
		mounted() {
			this.initializeComponent();
		},
		watch: {
			modelValue: {
				handler() {
					this.initializeComponent();
				},
				deep: true,
			},
		},
	};
</script>
